import {
  DocumentTextIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

interface UploadingReceiptItemProps {
  id: string;
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'error';
  error?: string;
  onRemove?: (id: string) => void;
}

const UploadingReceiptItem: React.FC<UploadingReceiptItemProps> = ({
  id,
  fileName,
  progress,
  status,
  error,
  onRemove,
}) => {
  const getStatusText = () => {
    switch (status) {
      case 'uploading':
        return `Uploading... ${progress}%`;
      case 'processing':
        return 'Processing...';
      case 'error':
        return 'Upload failed';
      default:
        return '';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'uploading':
        return 'text-blue-600 dark:text-blue-400';
      case 'processing':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getProgressBarColor = () => {
    switch (status) {
      case 'uploading':
        return 'bg-blue-600';
      case 'processing':
        return 'bg-yellow-600';
      case 'error':
        return 'bg-red-600';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <div className='p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 animate-fadeIn'>
      <div className='flex items-center justify-between mb-2'>
        <div className='flex items-center space-x-3 flex-1 min-w-0'>
          <DocumentTextIcon className='w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0' />
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate'>
              {fileName}
            </p>
            <div className='flex items-center gap-2 mt-1'>
              {status === 'error' && (
                <ExclamationCircleIcon className='w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0' />
              )}
              <p className={`text-xs ${getStatusColor()}`}>{getStatusText()}</p>
            </div>
          </div>
        </div>
        {status === 'error' && onRemove && (
          <button
            onClick={() => onRemove(id)}
            className='p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200'
            title='Remove'
          >
            <XMarkIcon className='w-5 h-5' />
          </button>
        )}
      </div>

      {/* Progress Bar */}
      {status !== 'error' && (
        <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2'>
          <div
            className={`${getProgressBarColor()} h-2 rounded-full transition-all duration-300 ${
              status === 'processing' ? 'animate-pulse' : ''
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Error Message */}
      {status === 'error' && error && (
        <div className='mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded'>
          <p className='text-xs text-red-600 dark:text-red-400'>
            Error: {error}
          </p>
        </div>
      )}
    </div>
  );
};

export default UploadingReceiptItem;
